import '../styles/pages/ProductPageShared.css';
import '../styles/pages/AggregatesPage.css';
import { m } from 'framer-motion';
import { fadeUp, staggerContainer, staggerItem, viewport } from '../motion/variants';

const heroStats = [
  { value: '5', label: 'Product categories' },
  { value: '10+', label: 'Aggregate types' },
  { value: 'On-site', label: 'Reliable delivery' },
];

const aggregateHighlights = [
  {
    title: 'High Strength',
    text: 'Withstands heavy loads and pressure in concrete and road works.',
  },
  {
    title: 'Durability',
    text: 'Resistant to weathering, water, and cracking over time.',
  },
  {
    title: 'Proper Gradation',
    text: 'Balanced particle sizes for better bonding and compaction.',
  },
  {
    title: 'Clean Material',
    text: 'Free from dust, clay, and organic impurities.',
  },
  {
    title: 'Angular Shape',
    text: 'Improves bonding in concrete and asphalt applications.',
  },
  {
    title: 'Good Drainage',
    text: 'Essential for roads, bridges, and infrastructure projects.',
  },
];

const aggregateCategories = [
  {
    id: 'fine-aggregates',
    title: 'Fine Aggregates',
    description: 'Fine-textured materials for finishing, paving, and compact filling work.',
    products: [
      {
        name: 'Dust / Quarry Dust',
        size: '0-4 mm',
        characteristics: 'Fine texture, easy compaction, good filling material',
        applications: 'Blocks, paving, finishing works, filling',
      },
      {
        name: 'Fine Chippings',
        size: '3-6 mm',
        characteristics: 'Smooth finishing, high bonding strength',
        applications: 'Terrazzo, tiles, fine concrete',
      },
    ],
  },
  {
    id: 'medium-chippings',
    title: 'Medium Chippings',
    description: 'Versatile chippings for residential concrete, slabs, and road construction.',
    products: [
      {
        name: 'Small Chippings',
        size: '5-10 mm',
        characteristics: 'Strong concrete bonding, uniform mixing',
        applications: 'Residential concrete, slabs, flooring',
      },
      {
        name: 'Half Inch Aggregate',
        size: '10-22 mm',
        characteristics: 'Most commonly used concrete aggregate, durable',
        applications: 'Concrete/zege, columns, beams',
      },
      {
        name: 'Medium Chippings',
        size: '10-14 mm',
        characteristics: 'Strong load-bearing capability',
        applications: 'Road construction, highways',
      },
    ],
  },
  {
    id: 'coarse-aggregates',
    title: 'Coarse Aggregates',
    description: 'Heavy-duty aggregates for foundations, structural concrete, and backfilling.',
    products: [
      {
        name: 'Three Quarter Aggregate',
        size: '22-33 mm',
        characteristics: 'Excellent structural stability and drainage',
        applications: 'Foundations, backfilling, heavy concrete',
      },
      {
        name: 'Large Three Quarter',
        size: '40 mm',
        characteristics: 'Heavy-duty aggregate with high compressive strength',
        applications: 'Industrial construction, infrastructure',
      },
    ],
  },
  {
    id: 'mixed-aggregates',
    title: 'Mixed Aggregates',
    description: 'Pre-blended mixtures for general-purpose construction and concrete mixing.',
    products: [
      {
        name: 'CRR Mixed Aggregate',
        size: 'Mixed Sizes',
        characteristics: 'Balanced aggregate mixture for general construction',
        applications: 'Concrete mixers, multipurpose works',
      },
    ],
  },
  {
    id: 'construction-stones',
    title: 'Construction Stones',
    description: 'Large stones for drainage, retaining walls, bridges, and heavy infrastructure.',
    products: [
      {
        name: 'Medium Stones',
        size: '50-100 mm',
        characteristics: 'Good drainage and stability',
        applications: 'Dry ports, drainage systems, coastal works',
      },
      {
        name: 'Large Construction Stones',
        size: '150-300 mm',
        characteristics: 'Very high strength and erosion resistance',
        applications: 'Bridges, retaining walls, house foundations',
      },
    ],
  },
];

function AggregatesPage() {
  return (
    <main className="aggregates-page" id="aggregates">
      <section className="product-page-hero aggregates-hero" aria-labelledby="aggregates-heading">
        <img
          className="product-page-hero-bg"
          src="/img/stones.jpeg"
          alt=""
          aria-hidden="true"
          loading="eager"
        />
        <div className="product-page-hero-inner">
          <m.span
            className="product-page-hero-eyebrow"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            Building Materials
          </m.span>
          <m.h1
            id="aggregates-heading"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.04, ease: [0.22, 1, 0.36, 1] }}
          >
            Construction Aggregates (Pebbles & Chippings)
          </m.h1>
          <m.p
            className="product-page-hero-lead"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            Construction aggregates are classified based on size, strength, durability, texture, and
            application. High-quality material improves the strength, stability, and lifespan of
            buildings, roads, bridges, and concrete works.
          </m.p>
          <m.div
            className="product-page-hero-stats"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
          >
            {heroStats.map((stat) => (
              <div key={stat.label} className="product-page-hero-stat">
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </div>
            ))}
          </m.div>
        </div>
      </section>

      <section className="aggregates-body" aria-labelledby="aggregates-table-heading">
        <div className="aggregates-body-inner">
          <m.div
            className="aggregates-highlights-grid"
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            variants={staggerContainer}
          >
            {aggregateHighlights.slice(0, 4).map((item) => (
              <m.article key={item.title} className="aggregates-highlight-card" variants={staggerItem}>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </m.article>
            ))}
          </m.div>

          <m.div
            className="product-page-section-header aggregates-table-header"
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            variants={fadeUp}
          >
            <h2 id="aggregates-table-heading">Categorized Aggregate Products & Characteristics</h2>
            <p>Browse our full range of pebbles, chippings, and stones grouped by size and application.</p>
          </m.div>

          {aggregateCategories.map((category) => (
            <m.section
              key={category.id}
              id={category.id}
              className="aggregates-card aggregates-category-section"
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              variants={fadeUp}
              aria-labelledby={`${category.id}-heading`}
            >
              <header className="aggregates-category-header">
                <div className="aggregates-category-header-text">
                  <h3 id={`${category.id}-heading`}>{category.title}</h3>
                  <p>{category.description}</p>
                </div>
              </header>

              <div className="aggregates-mobile-list" aria-label={`${category.title} products`}>
                {category.products.map((product) => (
                  <article key={product.name} className="aggregates-mobile-card">
                    <div className="aggregates-mobile-card-head">
                      <h4>{product.name}</h4>
                      <span className="aggregates-table-size">{product.size}</span>
                    </div>
                    <dl className="aggregates-mobile-details">
                      <div>
                        <dt>Product Characteristics</dt>
                        <dd>{product.characteristics}</dd>
                      </div>
                      <div>
                        <dt>Main Applications</dt>
                        <dd>{product.applications}</dd>
                      </div>
                    </dl>
                  </article>
                ))}
              </div>

              <div className="aggregates-table-scroll" tabIndex={0} aria-label={`${category.title} product table`}>
                <p className="aggregates-table-scroll-hint">Swipe horizontally to view all columns</p>
                <table className="aggregates-table">
                  <thead>
                    <tr>
                      <th>Product Name</th>
                      <th>Size Range</th>
                      <th>Product Characteristics</th>
                      <th>Main Applications</th>
                    </tr>
                  </thead>
                  <tbody>
                    {category.products.map((product) => (
                      <tr key={product.name}>
                        <td>
                          <span className="aggregates-table-category">{product.name}</span>
                        </td>
                        <td>
                          <span className="aggregates-table-size">{product.size}</span>
                        </td>
                        <td>{product.characteristics}</td>
                        <td>{product.applications}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </m.section>
          ))}
        </div>
      </section>
    </main>
  );
}

export default AggregatesPage;
